/** @jsxImportSource @emotion/react */
"use client";

import { useDebouncedValue } from "@packages/sdk";
import { useCallback, useEffect, useRef, useState } from "react";

import type { ComboboxSearchProps } from "./ComboboxSearch.emotion";
import { ComboboxSearch } from "./ComboboxSearch.emotion";
import type { ComboboxOption } from "./types";

export type { ComboboxOption } from "./types";

export interface ComboboxSearchAsyncConfig {
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Minimum characters before triggering search (default: 0) */
  minSearchLength?: number;
  /** Whether to fetch options on mount (default: true) */
  fetchOnMount?: boolean;
  /** Keep previous results visible while loading new ones (default: true) */
  preserveResultsWhileLoading?: boolean;
}

export interface ComboboxSearchAsyncProps<T = string>
  extends Omit<ComboboxSearchProps<T>, "options" | "isLoading"> {
  /** Override for default options, in case displaying the raw value doesn't work */
  defaultOptions?: ComboboxOption<T>[];
  /** Function to get and transform options. Receives the current search string. */
  getOptions: (search: string) => Promise<ComboboxOption<T>[]>;
  /** Async-specific configuration */
  asyncConfig?: ComboboxSearchAsyncConfig;
}

export function ComboboxSearchAsync<T = string>({
  defaultOptions,
  getOptions,
  asyncConfig = {},
  inputValue: controlledInputValue,
  onInputChange,
  config,
  ...props
}: ComboboxSearchAsyncProps<T>) {
  const {
    debounceMs = 300,
    minSearchLength = 0,
    fetchOnMount = true,
    preserveResultsWhileLoading = true,
  } = asyncConfig;
  const [internalInputValue, setInternalInputValue] = useState(
    props.value?.toString() ?? "",
  );
  const [options, setOptions] = useState<ComboboxOption<T>[]>(
    defaultOptions ??
      (props.value
        ? [
            {
              value: props.value,
              label: <>{props.value}</>,
              text: props.value.toString(),
            },
          ]
        : []),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedOnMount, setHasFetchedOnMount] = useState(false);
  const cachedOptionsRef = useRef<Map<string, ComboboxOption<T>[]>>(new Map());
  // the debounced-search effect below also fires immediately on mount (its
  // initial value is the starting inputValue, typically ""), duplicating
  // the fetchOnMount effect -- skip its first run in that exact case
  const skipInitialEmptyFetchRef = useRef(fetchOnMount);

  const inputValue =
    controlledInputValue !== undefined
      ? controlledInputValue
      : internalInputValue;

  useEffect(() => {
    if (controlledInputValue !== undefined) return;
    setInternalInputValue(props.value?.toString() ?? "");
  }, [props.value]);

  const handleInputChange = useCallback(
    (newValue: string) => {
      if (onInputChange) {
        onInputChange(newValue);
      } else {
        setInternalInputValue(newValue);
      }
    },
    [onInputChange],
  );

  const debouncedSearch = useDebouncedValue(inputValue, debounceMs);

  useEffect(() => {
    if (!fetchOnMount || hasFetchedOnMount) return;

    setHasFetchedOnMount(true);
    setIsLoading(true);

    getOptions("")
      .then((newOptions) => {
        cachedOptionsRef.current.set("", newOptions);
        setOptions(newOptions);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [fetchOnMount, hasFetchedOnMount, getOptions]);

  useEffect(() => {
    if (skipInitialEmptyFetchRef.current) {
      skipInitialEmptyFetchRef.current = false;
      if (debouncedSearch === "") return;
    }

    if (debouncedSearch.length < minSearchLength) {
      const cached = cachedOptionsRef.current.get("");
      if (cached) {
        setOptions(cached);
      }
      return;
    }

    const cached = cachedOptionsRef.current.get(debouncedSearch);
    if (cached) {
      setOptions(cached);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getOptions(debouncedSearch)
      .then((newOptions) => {
        if (!cancelled) {
          cachedOptionsRef.current.set(debouncedSearch, newOptions);
          setOptions(newOptions);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, minSearchLength, getOptions]);

  const showLoading =
    isLoading && (!preserveResultsWhileLoading || options?.length === 0);

  return (
    <ComboboxSearch<T>
      {...props}
      options={options}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isLoading={showLoading}
      config={{ ...config, filterLocally: false }}
    />
  );
}
